import {
  Badge,
  Box,
  Group,
  Paper,
  RingProgress,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconBook,
  IconCalculator,
  IconEye,
  IconPuzzle,
  IconStarFilled,
  IconTarget,
  TablerIcon,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { COGNITIVE_BAND_COLOR, STATUS_COLOR } from "@constants/resultColors";
import {
  AXIS_PRESENTATION,
  AxisKey,
  CAREER_FAMILY_KEYS_PER_AXIS,
  PerTestDetail,
} from "@utils/cognitiveProfile";
import { useCognitiveColor } from "../hooks/useCognitiveFillVariant";

interface CognitiveAxisCardProps {
  axisKey: AxisKey;
  detail?: PerTestDetail;
  isTopStrength: boolean;
  topStrengthRank?: number;
}

const ICON_BY_NAME: Record<string, TablerIcon> = {
  puzzle: IconPuzzle,
  book: IconBook,
  calculator: IconCalculator,
  target: IconTarget,
  eye: IconEye,
};

const formatMetricValue = (value: number | null): string => {
  if (value === null) return "—";
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(2);
};

export function CognitiveAxisCard({
  axisKey,
  detail,
  isTopStrength,
  topStrengthRank,
}: CognitiveAxisCardProps) {
  const { t } = useTranslation();
  const colorFor = useCognitiveColor();
  const presentation = AXIS_PRESENTATION[axisKey];
  const Icon = ICON_BY_NAME[presentation.iconName];
  const careerKeys = CAREER_FAMILY_KEYS_PER_AXIS[axisKey];

  const score = detail?.score ?? null;
  const status = detail?.status ?? null;
  const statusColor = status ? COGNITIVE_BAND_COLOR[status] : STATUS_COLOR.unavailable;

  return (
    <Paper
      p="lg"
      radius="md"
      withBorder
      shadow="xs"
      style={{
        borderTop: `4px solid var(--mantine-color-${presentation.color}-6)`,
        height: "100%",
      }}
    >
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap">
            <ThemeIcon size={42} radius="md" variant="light" color={presentation.color} aria-hidden>
              <Icon size={22} />
            </ThemeIcon>
            <Box>
              <Title order={4} c={`${presentation.color}.8`}>
                {t(`cognitiveProfile.axes.${axisKey}`)}
              </Title>
              <Text size="xs" c="dimmed" mt={2}>
                {t(`cognitiveProfile.axisDescriptions.${axisKey}`)}
              </Text>
            </Box>
          </Group>
          <RingProgress
            size={64}
            thickness={6}
            sections={[
              {
                value: score ?? 0,
                color: presentation.color,
              },
            ]}
            label={
              <Text ta="center" size="sm" fw={700}>
                {score === null ? "—" : Math.round(score)}
              </Text>
            }
          />
        </Group>

        <Group gap="xs">
          {status ? (
            <Badge color={colorFor(statusColor)} variant="filled" size="sm" tt="none">
              {t(`cognitiveProfile.bands.${status}`)}
            </Badge>
          ) : (
            <Badge color={colorFor("gray")} variant="filled" size="sm" tt="none">
              {t("cognitiveProfile.bands.unknown")}
            </Badge>
          )}
          {isTopStrength && (
            <Badge
              color={colorFor(presentation.color)}
              variant="filled"
              size="sm"
              tt="none"
              leftSection={<IconStarFilled size={12} />}
            >
              {t("cognitiveProfile.student.topStrengthBadge", {
                rank: topStrengthRank ?? 1,
              })}
            </Badge>
          )}
        </Group>

        {detail && Object.keys(detail.metricValues).length > 0 && (
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
              {t("cognitiveProfile.axisCard.measurements")}
            </Text>
            <Stack gap={2}>
              {Object.entries(detail.metricValues).map(([metric, value]) => (
                <Group key={metric} gap="xs" justify="space-between">
                  <Text size="sm" c="dimmed">
                    {t(`cognitiveProfile.metricLabels.${metric}`, { defaultValue: metric })}
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatMetricValue(value)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Box>
        )}

        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={6}>
            {t("cognitiveProfile.bands.frame.fieldsValuingThis")}
          </Text>
          <Group gap={6}>
            {careerKeys.map((key) => (
              <Badge
                key={key}
                color={colorFor(presentation.color)}
                variant="filled"
                size="sm"
                tt="none"
              >
                {t(key)}
              </Badge>
            ))}
          </Group>
        </Box>

        <Text size="xs" c="dimmed" mt="auto" fs="italic">
          {t(`cognitiveProfile.discussionPrompts.${axisKey}`)}
        </Text>
      </Stack>
    </Paper>
  );
}
